import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Action, Select, Selector, State, StateContext, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { AuthUser } from "~app/core/auth/core/auth-user.model";
import { AuthState } from "~app/core/auth/core/auth.state";
import { SubscriptionCommandService } from "./subscription-command.service";
import { SubscriptionQueryService } from "./subscription-query.service";
import {
  AddMemberInsuranceStatus,
  AddMemberInsuranceStatusFailure,
  AddMemberInsuranceStatusSuccess,
  LoadMemberId,
  LoadMemberIdFailure,
  LoadMemberIdSuccess,
  LoadSubscriptionForm,
  LoadSubscriptionFormFailure,
  LoadSubscriptionFormSuccess,
  LoadSubscriptionPeriods,
  LoadSubscriptionPeriodsFailure,
  LoadSubscriptionPeriodsSuccess,
  LoadSubscriptionStatus,
  LoadSubscriptionStatusFailure,
  LoadSubscriptionStatusSuccess,
  ProceedSubscriptionPayment,
  ProceedSubscriptionPaymentFailure,
  ProceedSubscriptionPaymentSuccess,
  ReportReclamationValidation,
  ReportReclamationValidationFailure,
  ReportReclamationValidationSuccess,
  SeeParticularConditions,
  SeeParticularConditionsFailure,
  SeeParticularConditionsSuccess,
  SeeSubscriptionReceipt,
  SeeSubscriptionReceiptFailure,
  SeeSubscriptionReceiptSuccess
} from "./subscription.actions";
import {
  MemberInsuranceViewModel,
  PaymentErrorModel,
  PaymentReceipt,
  SubscriptionFormViewModel,
  SubscriptionNoticePreselection,
  SubscriptionPeriod
} from "./subscription.model";


export class SubscriptionStateModel {
  loadingPremiumData: boolean;
  loadingPeriods: boolean;
  loadingMemberId: boolean;
  loadingReport: boolean;
  formModel: SubscriptionFormViewModel;
  periods: SubscriptionPeriod[];
  validPeriod: boolean;
  memberId: string;
  //payment
  paymentError: string;
  paymentReceipt: PaymentReceipt;
  paymentErrorInfo: PaymentErrorModel;
  loadingStatus: boolean;
  souscriptionStatus: MemberInsuranceViewModel[];
  addMemberInsuranceStatusError: string;
  subscriptionNoticePreselection: SubscriptionNoticePreselection;
  savingReportReclamationValidation: boolean;
  processingFARPSubscription: boolean;
}

@State<SubscriptionStateModel>({
  name: "subscription",
  defaults: new SubscriptionStateModel(),
})
@Injectable()
export class SubscriptionState implements OnDestroy {
  private destroy$ = new Subject<void>();

  @Select(AuthState.user) user$: Observable<AuthUser>;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private store: Store,
    private queryService: SubscriptionQueryService,
    private commandService: SubscriptionCommandService
  ) {}

  @Selector()
  static loadingPremiumData(state: SubscriptionStateModel): boolean {
    return state.loadingPremiumData;
  }

  @Selector()
  static loadingStatus(state: SubscriptionStateModel): boolean {
    return state.loadingStatus;
  }

  @Selector()
  static savingReportReclamationValidation(state: SubscriptionStateModel): boolean {
    return state.savingReportReclamationValidation;
  }
  

  @Selector()
  static loadingPeriods(state: SubscriptionStateModel): boolean {
    return state.loadingPeriods;
  }

  @Selector()
  static loadingReport(state: SubscriptionStateModel): boolean {
    return state.loadingReport;
  }

  @Selector()
  static loadingMemberId(state: SubscriptionStateModel): boolean {
    return state.loadingMemberId;
  }

  @Selector()
  static formModel(state: SubscriptionStateModel): SubscriptionFormViewModel {
    return state.formModel;
  }

  @Selector()
  static periods(state: SubscriptionStateModel): SubscriptionPeriod[] {
    return state.periods;
  }

  @Selector()
  static validPeriod(state: SubscriptionStateModel): boolean {
    return !!state.formModel;
  }
  
  @Selector()
  static memberId(state: SubscriptionStateModel): string {
    return state.memberId;
  }

  @Selector()
  static paymentError(state: SubscriptionStateModel): string {
    return state.paymentError;
  }

  @Selector()
  static paymentErrorInfo(state: SubscriptionStateModel): PaymentErrorModel {
    return state.paymentErrorInfo;
  }

  @Selector()
  static paymentReceipt(state: SubscriptionStateModel): PaymentReceipt {
    return state.paymentReceipt;
  }

  @Selector()
  static souscriptionStatus(state: SubscriptionStateModel): MemberInsuranceViewModel[] {
    return state.souscriptionStatus;
  }

  @Selector()
  static processingFARPSubscription(state: SubscriptionStateModel): boolean {
    return state.processingFARPSubscription;
  }

  @Selector()
  static userCanEditForm(state: SubscriptionStateModel): boolean {
    const s =  state.souscriptionStatus &&  state.souscriptionStatus.length>0 ? state.souscriptionStatus[0]: null;
    if (!s) {
      return false;
    }
    return !s.isExemptFromInsurance
        && (s.isActiveExercise || s.isWaitingForInsurance);
  }

  @Selector()
  static isSubscriptionBlocked(state: SubscriptionStateModel): boolean {
     return   state.periods == null || state.periods.length ==0 || state.periods.some(p=>p.isSubscriptionBlocked);
  }

  @Selector()
  static subscriptionNoticePreselection(state: SubscriptionStateModel): SubscriptionNoticePreselection {
     return state.subscriptionNoticePreselection;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //subscription form model
  @Action(LoadSubscriptionForm)
  onLoadSubscriptionFormViewModel(
    {  patchState, dispatch }: StateContext<SubscriptionStateModel>,
    { periodId, subscriptionNoticeId, memberId}: LoadSubscriptionForm
  ) {
    patchState({loadingPremiumData: true});
    return this.queryService.getSubscriptionForm(periodId, subscriptionNoticeId, memberId).pipe(
        tap((res)=> dispatch(new LoadSubscriptionFormSuccess(res))),
        catchError((error)=>dispatch(new LoadSubscriptionFormFailure(error)))
    );
  }
  @Action(LoadSubscriptionFormSuccess)
  onLoadSubscriptionFormSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data }: LoadSubscriptionFormSuccess
  ) {
     //needed in fullstroy in case user dont see proper surplus
     console.log('SubscriptionFormData ' + JSON.stringify(data));
    patchState({ formModel: data, loadingPremiumData: false, paymentErrorInfo: null, paymentReceipt: null, paymentError: null});
  }

  @Action(LoadSubscriptionFormFailure)
  onLoadPremiumBaseFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    { error }: LoadSubscriptionFormFailure
  ) {
    console.log(error);
    patchState({ formModel: null, loadingPremiumData: false, paymentErrorInfo: null, paymentReceipt: null, paymentError: null});
  }

  //periods
  @Action(LoadSubscriptionPeriods)
  onLoadSubscriptionPeriods(
    {  patchState, dispatch }: StateContext<SubscriptionStateModel>,
    {  memberId }: LoadSubscriptionPeriods
  ) {
    patchState({loadingPeriods: true});
    return this.queryService.getPeriods(memberId).pipe(
        tap((res)=> dispatch(new LoadSubscriptionPeriodsSuccess(res))),
        catchError((error)=>dispatch(new LoadSubscriptionPeriodsFailure(error)))
    );
  }
  @Action(LoadSubscriptionPeriodsSuccess)
  onLoadSubscriptionPeriodsSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data }: LoadSubscriptionPeriodsSuccess
  ) {
    patchState({ periods: data, loadingPeriods: false, paymentErrorInfo: null, paymentReceipt: null, paymentError: null});
  }

  @Action(LoadSubscriptionPeriodsFailure)
  onLoadSubscriptionPeriodsFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    { error }: LoadSubscriptionPeriodsFailure
  ) {
    console.log(error);
    patchState({ periods: null, loadingPeriods: false, paymentErrorInfo: null, paymentReceipt: null, paymentError: null});
  }

  //memberId
  @Action(LoadMemberId )
  onLoadMemberId (
    {  patchState, dispatch }: StateContext<SubscriptionStateModel>,
    {  permitNumber }: LoadMemberId
  ) {
    patchState({loadingMemberId: true});
    return this.queryService.getMemberIdByPermitNumber(permitNumber).pipe(
        tap((res)=> dispatch(new LoadMemberIdSuccess(res))),
        catchError((error)=>dispatch(new LoadMemberIdFailure(error)))
    );
  }
  @Action(LoadMemberIdSuccess)
  onLoadMemberIdSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data }: LoadMemberIdSuccess
  ) {
    patchState({ memberId: data, loadingMemberId: false});
  }

  @Action(LoadMemberIdFailure)
  onLoadMemberIdFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    { error }: LoadMemberIdFailure
  ) {
    console.log(error);
    patchState({ memberId: null, loadingMemberId: false});
  }

  //payment
  @Action(ProceedSubscriptionPayment)
  onProceedSubscriptionPayment(
    { dispatch, patchState }: StateContext<SubscriptionStateModel>,
    { selectedPremiumInfo }: ProceedSubscriptionPayment
  ) {
    patchState({ paymentError: "", processingFARPSubscription: true });
    return this.commandService
      .proceedSubscriptionPayment(selectedPremiumInfo)
      .pipe(
        tap(result => dispatch(new ProceedSubscriptionPaymentSuccess(result.confirmationModel))),
        tap(result => {if (!result.isStatusResponsibilityInsuranceExist &&  result.memberInsuranceStatus)
        dispatch(new AddMemberInsuranceStatus(selectedPremiumInfo.memberId, result.memberInsuranceStatus))}),
        catchError((err) => dispatch(new ProceedSubscriptionPaymentFailure(err, selectedPremiumInfo)))
      );
  }
  @Action(ProceedSubscriptionPaymentSuccess)
  onProceedSubscriptionPaymentSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data }: ProceedSubscriptionPaymentSuccess
  ) {
    patchState({
      paymentReceipt: data,
      processingFARPSubscription: false
    });
  }

  @Action(ProceedSubscriptionPaymentFailure)
  onProceedSubscriptionPaymentFailure(
    { patchState, dispatch }: StateContext<SubscriptionStateModel>,
    { error, selectedPremiumInfo }: ProceedSubscriptionPaymentFailure
  ) {

    console.log('Error ' + JSON.stringify(error));
    console.log('Selection ' + JSON.stringify(selectedPremiumInfo));
    console.log('ErrorMessage ' + JSON.stringify(error?.error?.errorMessage));

    patchState({
      processingFARPSubscription: false
    });

    // If the server timed out AFTER Moneris approved the payment.In this case we treat the
    // response as a success so the user sees the confirmation page instead of a restart dialog.
    const paymentApprovedDespiteServerError =
      error?.error?.result?.confirmationModel?.transactionResult?.toUpperCase() === 'ACCEPTED';
    if (paymentApprovedDespiteServerError) {
      dispatch(new ProceedSubscriptionPaymentSuccess(error.error.result.confirmationModel));
      return;
    }

    const errorStatus: string = error?.error?.confirmationModel?.message;
    const errorCausedByInvalidSubscriptionNotice:  boolean = error?.error?.isInvalidSubscriptionNotice;
    const errorCausedByInvalidAmount: boolean = error?.error?.isInvalidAmount;
    const errorUnexpectedError: boolean = error?.error?.isUnexpectedError;
    let message = "";
    let requiresRestartForm = false;

    if (errorCausedByInvalidSubscriptionNotice) {
      message = this.translate.instant("subscription.form.payment.invalidSubscriptionNotice");
      requiresRestartForm = true;
    }

    if (errorCausedByInvalidAmount) {
      message = this.translate.instant("subscription.form.payment.invalidAmount");
      requiresRestartForm = true;
    }
    
    if (errorUnexpectedError) {
      message = this.translate.instant("subscription.form.payment.unexpectedError");
      requiresRestartForm = true;
    }

    const criticalError = this.translate.instant("subscription.form.payment.criticalError");

    if (error.status == 404) {
      // No bills found
      message = this.translate.instant("subscription.form.payment.noBillsFound");
    } else if (error.status == 400) {
      // Moneris error or wrong amount
      message = this.translate.instant(
        "subscription.form.payment.sumOrMonerisError"
      );
    } else if (
      !!errorStatus &&
      errorStatus.toUpperCase().indexOf("DECLINED") >= 0
    ) {
      message = this.translate.instant(
        "subscription.form.payment.transactionError"
      );
    } else {
      // Other error
      if (!message) {      
        message = this.translate.instant("subscription.form.payment.paymentError");
      }      
    }
    patchState({ paymentError: requiresRestartForm ? criticalError :message, 
                     paymentErrorInfo:  { requiresRestartForm: requiresRestartForm, message: message } });
  }

  @Action(SeeParticularConditions)
  onSeeParticularConditions(
    { patchState,dispatch }: StateContext<SubscriptionStateModel>,
    { memberId, financialYear, permitNumber }: SeeParticularConditions
  ) {
    patchState( {loadingReport: true});
    return this.queryService.getParticularConditionReport(memberId, financialYear).pipe(
      tap((data) =>
        dispatch(new SeeParticularConditionsSuccess(data, financialYear, permitNumber))
      ),
      catchError((err) => dispatch(new SeeParticularConditionsFailure(err)))
    );
  }

  @Action(SeeParticularConditionsSuccess)
  onSeeParticularConditionsSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data, financialYear, permitNumber }: SeeParticularConditionsSuccess
  ) {
    patchState( {loadingReport: false});
    this.download({
      data: data,
      name: `ConditionsParticulieres_${permitNumber}_${financialYear}.pdf`
    });
  }

  @Action(SeeParticularConditionsFailure)
  onSeeParticularConditionsFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    {  }: SeeParticularConditionsFailure
  ) {
    patchState( {loadingReport: false});   
  }


  @Action(SeeSubscriptionReceipt)
  onSeeSubscriptionReceipt(
    { patchState, dispatch }: StateContext<SubscriptionStateModel>,
    { memberId, id, description, permitNumber }: SeeSubscriptionReceipt
  ) {
    patchState( {loadingReport: true});
    return this.queryService.getSubscriptionReceipt(memberId, id).pipe(
      tap((data) =>
        dispatch(new SeeSubscriptionReceiptSuccess(data, id, description,permitNumber))
      ),
      catchError((err) => dispatch(new SeeSubscriptionReceiptFailure(err)))
    );
  }

  @Action(SeeSubscriptionReceiptSuccess)
  onSeeSubscriptionReceiptSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data, id, description, permitNumber }: SeeSubscriptionReceiptSuccess
  ) {
    patchState( {loadingReport: false});
    this.download({
      data: data,
      name: `Receipt_${permitNumber}_${id}.pdf`
    });
  }

  @Action(SeeSubscriptionReceiptFailure)
  onSeeSubscriptionReceiptFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    {  }: SeeSubscriptionReceiptFailure
  ) {
    patchState( {loadingReport: false});   
  }

  download(file: { data: Blob; name: string }): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(file.data);
    link.download = file.name;
    link.click();
  }

  //SouscriptionStatus
  @Action(LoadSubscriptionStatus)
  onLoadSubscriptionStatus(
    {  patchState, dispatch }: StateContext<SubscriptionStateModel>,
    {  memberId }: LoadSubscriptionStatus
  ) {
    patchState({loadingStatus: true});
    return this.queryService.getSubscriptionStatus(memberId).pipe(
        tap((res)=> dispatch(new LoadSubscriptionStatusSuccess(res))),
        catchError((error)=>dispatch(new LoadSubscriptionStatusFailure(error)))
    );
  }
  @Action(LoadSubscriptionStatusSuccess)
  onLoadSubscriptionStatusSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { data }: LoadSubscriptionStatusSuccess
  ) {
    //needed in fullstroy in case user dont see card (for debug)
    console.log('SubscriptionStatus ' + JSON.stringify(data));
    patchState({ souscriptionStatus: data, loadingStatus: false});
  }

  @Action(LoadSubscriptionStatusFailure)
  onLoadSubscriptionStatusFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    { error }: LoadSubscriptionStatusFailure
  ) {
    console.log(error);
    patchState({ souscriptionStatus: null, loadingStatus: false});
  }

  @Action(AddMemberInsuranceStatus)
  onAddMemberInsuranceStatus(
    { dispatch, patchState }: StateContext<SubscriptionStateModel>,
    {
      memberId,
      insuranceStatus
    }: AddMemberInsuranceStatus
  ) {
    patchState({ addMemberInsuranceStatusError: "" });
    return this.commandService
      .addMemberInsuranceStatus(memberId, insuranceStatus)
      .pipe(
        tap(() => dispatch(new AddMemberInsuranceStatusSuccess())),
        catchError((error) => dispatch(new AddMemberInsuranceStatusFailure(error)))
      );
  }

  @Action(ReportReclamationValidation)
  onReportReclamationValidation(
    { dispatch, patchState }: StateContext<SubscriptionStateModel>,
    {
      memberId,
      financialYear,
      isReclamationReported,
      surplusPremiumId,
      subscriptionNoticeId
    }: ReportReclamationValidation
  ) {
    patchState({ subscriptionNoticePreselection: null, savingReportReclamationValidation: true });
    return this.commandService
      .reportReclamationValidation(memberId, financialYear, isReclamationReported, surplusPremiumId, subscriptionNoticeId)
      .pipe(
        tap((result) => dispatch(new ReportReclamationValidationSuccess(result))),
        catchError((error) => dispatch(new ReportReclamationValidationFailure(error)))
      );
  }

  @Action(ReportReclamationValidationSuccess)
  onReportReclamationValidationSuccess(
    { patchState }: StateContext<SubscriptionStateModel>,
    { subscriptionNoticePreselection }: ReportReclamationValidationSuccess
  ) {
    patchState({ subscriptionNoticePreselection, savingReportReclamationValidation: false});
  }

  @Action(ReportReclamationValidationFailure)
  onReportReclamationValidationFailure(
    { patchState }: StateContext<SubscriptionStateModel>,
    { error }: ReportReclamationValidationFailure
  ) {
    console.log(error);
    patchState({ subscriptionNoticePreselection: null, savingReportReclamationValidation: false});
  }

  
}
